import React from "react"
import { ProviderProps } from "./types"
import {
  GoogleMapsId,
  GoogleMapsType,
  GOOGLE_MAPS_IDS,
  MapboxStyle,
  MAPBOX_STYLES,
  MapProvider,
  useGoogleMapsMapId,
  useGoogleMapsTypeId,
  useMapProvider,
  useMapboxMapStyle,
} from "../../lib/hooks/useMapStyle"

export type MapStyleSetting = "light" | "dark" | "color" | "satellite" | undefined

export type MapStyleSettingSetter = (setting: MapStyleSetting) => void

interface MapSettingsContextProps {
  mapProvider: MapProvider
  setMapProvider: (val: MapProvider) => void
  mapStyleSetting: MapStyleSetting
  setMapStyleSetting: MapStyleSettingSetter
  mapboxStyle: MapboxStyle
  googleMapsType: GoogleMapsType
  googleMapsId: GoogleMapsId
}

const MapSettingsContext = React.createContext<MapSettingsContextProps | undefined>(undefined)

const MapSettingsProvider: React.FC<ProviderProps> = ({ children }) => {
  const [mapProvider, setMapProvider] = useMapProvider()
  const [mapboxStyle, setMapboxStyle] = useMapboxMapStyle()
  const [googleMapsType, setGoogleMapsType] = useGoogleMapsTypeId()
  const [googleMapsId, setGoogleMapsId] = useGoogleMapsMapId()
  const [mapStyleSetting, setMapStyleSetting] = React.useState<MapStyleSetting>()

  // initialize style values
  React.useEffect(() => {
    if (mapProvider === "mapbox") {
      switch (mapboxStyle) {
        case MAPBOX_STYLES.DARK:
          setMapStyleSetting("dark")
          break
        case MAPBOX_STYLES.COLOR:
          setMapStyleSetting("color")
          break
        case MAPBOX_STYLES.SATELLITE:
          setMapStyleSetting("satellite")
          break
        case MAPBOX_STYLES.LIGHT:
        default:
          setMapStyleSetting("light")
          break
      }
      return
    }

    if (mapProvider === "google_maps") {
      if (googleMapsId === GOOGLE_MAPS_IDS.LIGHT && googleMapsType === "roadmap") {
        setMapStyleSetting("light")
        return
      }

      if (googleMapsId === GOOGLE_MAPS_IDS.DARK && googleMapsType === "roadmap") {
        setMapStyleSetting("dark")
        return
      }

      if (googleMapsId === GOOGLE_MAPS_IDS.COLOR && googleMapsType === "roadmap") {
        setMapStyleSetting("color")
        return
      }

      if (googleMapsType === "hybrid") {
        setMapStyleSetting("satellite")
        return
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapProvider, mapboxStyle, googleMapsType, googleMapsId, setMapStyleSetting])

  // when style is changed, update local storage
  React.useEffect(() => {
    if (!mapStyleSetting) return

    if (mapStyleSetting === "light") {
      setMapboxStyle(MAPBOX_STYLES.LIGHT)
      setGoogleMapsId(GOOGLE_MAPS_IDS.LIGHT)
      setGoogleMapsType("roadmap")
    }

    if (mapStyleSetting === "dark") {
      setMapboxStyle(MAPBOX_STYLES.DARK)
      setGoogleMapsId(GOOGLE_MAPS_IDS.DARK)
      setGoogleMapsType("roadmap")
    }

    if (mapStyleSetting === "color") {
      setMapboxStyle(MAPBOX_STYLES.COLOR)
      setGoogleMapsId(GOOGLE_MAPS_IDS.COLOR)
      setGoogleMapsType("roadmap")
      return
    }

    if (mapStyleSetting === "satellite") {
      setMapboxStyle(MAPBOX_STYLES.SATELLITE)
      setGoogleMapsId(GOOGLE_MAPS_IDS.SATELLITE)
      setGoogleMapsType("hybrid")
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyleSetting, setMapStyleSetting])

  const contextValue = React.useMemo(
    () => ({
      mapProvider,
      setMapProvider,
      mapStyleSetting,
      setMapStyleSetting,
      mapboxStyle,
      googleMapsType,
      googleMapsId,
    }),
    [mapProvider, mapStyleSetting, mapboxStyle, googleMapsType, googleMapsId, setMapProvider],
  )

  return <MapSettingsContext.Provider value={contextValue}>{children}</MapSettingsContext.Provider>
}

export const useMapSettings = () => {
  const context = React.useContext(MapSettingsContext)
  if (!context) throw new Error("Must be called under MapSettingsProvider")
  return context
}

export default MapSettingsProvider
